import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

/**
 * PayPal checkout modal – runs the NCR `/paypal/create-order` flow, opens the
 * returned checkoutUrl in a popup, and polls `GET /shop/shop-details/:id`
 * after the popup closes until the wallet / subscription / one-time state
 * reflects the payment (or a 90s timeout hits).
 */
const VERIFY_SCHEDULE_MS = [2000, 3000, 5000, 8000, 10000, 10000, 10000, 10000, 10000, 10000];

const STEP = {
    CREATING: "CREATING",
    CHECKOUT: "CHECKOUT",
    VERIFYING: "VERIFYING",
    SUCCESS: "SUCCESS",
    FAILED: "FAILED",
};

export const PayPalCheckoutModal = ({
    open,
    onClose,
    onSuccess,
    productId,
    productType, // "RECHARGE" | "SUBSCRIPTION" | "ONETIMEPAYMENT"
    productLabel, // e.g. "Buy 750 Credits" – shown in the modal
    baseline, // { recharge_credits, plan_id, cycle_end, paidPackCodes:Set, ... }
    expectedPackCode, // only used for ONETIMEPAYMENT verification
}) => {
    const [step, setStep] = useState(STEP.CREATING);
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const popupRef = useRef(null);
    const popupPollRef = useRef(null);
    const verifyAbortedRef = useRef(false);

    const cleanup = useCallback(() => {
        if (popupPollRef.current) {
            clearInterval(popupPollRef.current);
            popupPollRef.current = null;
        }
        if (popupRef.current && !popupRef.current.closed) {
            try {
                popupRef.current.close();
            } catch {
                /* ignore */
            }
        }
        popupRef.current = null;
    }, []);

    useEffect(() => () => cleanup(), [cleanup]);

    // Kick off the create-order + popup open as soon as the modal opens.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        verifyAbortedRef.current = false;
        setError(null);
        setOrder(null);
        setStep(STEP.CREATING);

        (async () => {
            try {
                const o = await api.createPayPalOrder(productId, productType);
                if (cancelled) return;
                setOrder(o);
                const url = o.checkoutUrl || o.paymentUrl;
                if (!url) {
                    setError("Payment URL missing in response");
                    setStep(STEP.FAILED);
                    return;
                }
                // Open popup (centered). Fallback to same-tab if blocked.
                const w = 520;
                const h = 720;
                const left = window.screenX + (window.outerWidth - w) / 2;
                const top = window.screenY + (window.outerHeight - h) / 2;
                const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no`;
                const popup = window.open(url, "paypal-checkout", features);
                if (!popup) {
                    // popup blocked – fall back to same-tab (not ideal but works)
                    window.location.href = url;
                    return;
                }
                popupRef.current = popup;
                setStep(STEP.CHECKOUT);

                // Poll popup.closed; when closed, start verification.
                popupPollRef.current = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(popupPollRef.current);
                        popupPollRef.current = null;
                        if (verifyAbortedRef.current) return;
                        setStep(STEP.VERIFYING);
                        runVerification(o).catch((e) => {
                            console.error(e);
                            setError(e?.message || "Verification failed");
                            setStep(STEP.FAILED);
                        });
                    }
                }, 600);
            } catch (e) {
                if (cancelled) return;
                setError(e?.message || "Failed to start checkout");
                setStep(STEP.FAILED);
            }
        })();

        return () => {
            cancelled = true;
            verifyAbortedRef.current = true;
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, productId, productType]);

    const runVerification = async (o) => {
        for (const wait of VERIFY_SCHEDULE_MS) {
            if (verifyAbortedRef.current) return;
            await sleep(wait);
            if (verifyAbortedRef.current) return;
            try {
                const fresh = await api.me();
                if (isConfirmed(fresh, productType, baseline, expectedPackCode)) {
                    setStep(STEP.SUCCESS);
                    onSuccess?.({ order: o, shop: fresh });
                    return;
                }
                // For one-time we may additionally need to re-fetch master packs
                if (productType === "ONETIMEPAYMENT" && expectedPackCode) {
                    const packs = await api.getOneTimePacks();
                    const hit = packs.find((p) => p.packCode === expectedPackCode);
                    if (hit?.isOneTimePayment) {
                        setStep(STEP.SUCCESS);
                        onSuccess?.({ order: o, shop: fresh, packs });
                        return;
                    }
                }
            } catch {
                /* retry */
            }
        }
        if (verifyAbortedRef.current) return;
        setError("We couldn't confirm the payment yet. If you completed it, refresh the page in a minute.");
        setStep(STEP.FAILED);
    };

    const retry = () => {
        verifyAbortedRef.current = false;
        setStep(STEP.CREATING);
    };

    const forceVerify = async () => {
        if (!order) return;
        setStep(STEP.VERIFYING);
        try {
            await runVerification(order);
        } catch (e) {
            setError(e?.message || "Verification failed");
            setStep(STEP.FAILED);
        }
    };

    const closeAll = () => {
        verifyAbortedRef.current = true;
        cleanup();
        onClose?.();
    };

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 px-4"
            data-testid="paypal-checkout-modal"
        >
            <div className="ap-card w-full max-w-md p-6 md:p-7 relative scale-in">
                <button
                    onClick={closeAll}
                    data-testid="paypal-modal-close"
                    className="absolute top-4 right-4 text-slate-400 hover:text-white"
                >
                    <X size={18} />
                </button>

                <div className="flex flex-col items-center text-center gap-4">
                    <StepIcon step={step} />
                    <h3 className="text-xl font-bold">{titleForStep(step)}</h3>
                    <p
                        className="text-sm"
                        style={{ color: "var(--ap-text-dim)" }}
                        data-testid="paypal-modal-subtitle"
                    >
                        {subtitleForStep(step, productLabel, order, error)}
                    </p>

                    {step === STEP.CHECKOUT && order?.checkoutUrl && (
                        <div className="flex flex-col gap-2 w-full mt-2">
                            <a
                                href={order.checkoutUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="paypal-open-checkout-link"
                                className="ap-btn-ghost text-xs flex items-center justify-center gap-2"
                            >
                                <ExternalLink size={14} /> Re-open PayPal window
                            </a>
                            <button
                                onClick={forceVerify}
                                data-testid="paypal-force-verify"
                                className="ap-btn-primary text-sm"
                            >
                                I&apos;ve completed the payment
                            </button>
                        </div>
                    )}

                    {step === STEP.FAILED && (
                        <div className="flex gap-2 w-full mt-2">
                            <button
                                onClick={closeAll}
                                className="ap-btn-ghost flex-1"
                                data-testid="paypal-close-failed"
                            >
                                Close
                            </button>
                            <button
                                onClick={retry}
                                className="ap-btn-primary flex-1"
                                data-testid="paypal-retry"
                            >
                                Try again
                            </button>
                        </div>
                    )}

                    {step === STEP.SUCCESS && (
                        <button
                            onClick={closeAll}
                            className="ap-btn-primary w-full mt-2"
                            data-testid="paypal-done"
                        >
                            Done
                        </button>
                    )}

                    {order && (
                        <div
                            className="text-[10px] font-mono mt-3"
                            style={{ color: "var(--ap-text-muted)" }}
                        >
                            Order #{order.orderNo || order.orderId}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

function StepIcon({ step }) {
    const style = {
        width: 56,
        height: 56,
        borderRadius: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };
    if (step === STEP.SUCCESS) {
        return (
            <div style={{ ...style, background: "rgba(34,197,94,0.15)", color: "#4ade80" }}>
                <ShieldCheck size={28} />
            </div>
        );
    }
    if (step === STEP.FAILED) {
        return (
            <div style={{ ...style, background: "rgba(239,68,68,0.15)", color: "#fca5a5" }}>
                <AlertTriangle size={28} />
            </div>
        );
    }
    return (
        <div style={{ ...style, background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>
            <Loader2 size={28} className="animate-spin" />
        </div>
    );
}

function titleForStep(step) {
    switch (step) {
        case STEP.CREATING:
            return "Preparing secure checkout…";
        case STEP.CHECKOUT:
            return "Complete payment in PayPal";
        case STEP.VERIFYING:
            return "Confirming your payment…";
        case STEP.SUCCESS:
            return "Payment confirmed";
        case STEP.FAILED:
            return "Payment not completed";
        default:
            return "";
    }
}

function subtitleForStep(step, productLabel, order, error) {
    switch (step) {
        case STEP.CREATING:
            return `Creating a secure order for ${productLabel || "your payment"}.`;
        case STEP.CHECKOUT:
            return `A PayPal window has opened in a new tab. Complete the payment for ${productLabel || "your order"}, then return to this window — we'll verify automatically.`;
        case STEP.VERIFYING:
            return "This usually takes a few seconds. We're checking with PayPal to confirm and update your account.";
        case STEP.SUCCESS:
            return `Your ${productLabel || "payment"} has been applied to your account.`;
        case STEP.FAILED:
            return error || "Please try again, or contact support if the issue persists.";
        default:
            return "";
    }
}

function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

function isConfirmed(shop, productType, baseline, expectedPackCode) {
    if (!shop || !baseline) return false;
    if (productType === "RECHARGE") {
        return (shop.credits?.recharge_credits || 0) > (baseline.recharge_credits || 0);
    }
    if (productType === "SUBSCRIPTION") {
        const planChanged =
            (shop.subscription?.plan_id || "") !== (baseline.plan_id || "");
        const cycleAdvanced =
            shop.subscription?.cycle_end &&
            baseline.cycle_end &&
            new Date(shop.subscription.cycle_end) > new Date(baseline.cycle_end);
        return planChanged || cycleAdvanced;
    }
    if (productType === "ONETIMEPAYMENT") {
        // Verified via master packs re-fetch in the caller loop.
        return false;
    }
    return false;
}

// Helper exported for callers to construct the baseline snapshot.
export function snapshotShopForVerification(shop) {
    return {
        recharge_credits: shop?.credits?.recharge_credits || 0,
        plan_id: shop?.subscription?.plan_id || "",
        cycle_end: shop?.subscription?.cycle_end || "",
    };
}
